// =============================================================================
// TwoFactorSettings — authenticator-app two-factor login (Settings → Account)
// =============================================================================
// Off → "Set up": confirm password → scan QR (or tap "Open in authenticator
// app" on a phone, or type the key) → enter a code → recovery codes, shown
// once. On → status, new recovery codes (needs a code), turn off (password +
// code). Backend: routes/auth.ts /2fa/*, services/twoFactor.ts.
//
// The QR encoder (qrcode-generator) is dynamically imported so it only loads
// when someone actually starts setup.
// =============================================================================

import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { Button, Input, Spinner } from './ui';
import { toast } from './Toast';

type Status = { enabled: boolean; enabledAt: string | null; recoveryCodesLeft: number };
type Step =
  | { kind: 'idle' }
  | { kind: 'password' }
  | { kind: 'scan'; secret: string; otpauthUrl: string }
  | { kind: 'codes'; codes: string[]; fresh: boolean }
  | { kind: 'regenerate' }
  | { kind: 'disable' };

const errText = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

function QrCode({ text }: { text: string }) {
  const [cells, setCells] = useState<boolean[][] | null>(null);
  useEffect(() => {
    let alive = true;
    void import('qrcode-generator').then(({ default: qrcode }) => {
      const qr = qrcode(0, 'M');
      qr.addData(text);
      qr.make();
      const n = qr.getModuleCount();
      const grid = Array.from({ length: n }, (_, r) => Array.from({ length: n }, (_, c) => qr.isDark(r, c)));
      if (alive) setCells(grid);
    });
    return () => {
      alive = false;
    };
  }, [text]);

  if (!cells) return <div className="w-44 h-44 flex items-center justify-center"><Spinner size={16} /></div>;
  const n = cells.length;
  const quiet = 4; // the spec's quiet zone; scanners need it
  return (
    <svg
      viewBox={`0 0 ${n + quiet * 2} ${n + quiet * 2}`}
      className="w-44 h-44 rounded-aim"
      shapeRendering="crispEdges"
      role="img"
      aria-label="QR code for your authenticator app"
    >
      {/* Always black on white, whatever the theme: dark-on-light is what
          every scanner expects. */}
      <rect width="100%" height="100%" fill="#fff" />
      {cells.flatMap((row, r) =>
        row.map((dark, c) => (dark ? <rect key={`${r}-${c}`} x={c + quiet} y={r + quiet} width="1" height="1" fill="#000" /> : null))
      )}
    </svg>
  );
}

function RecoveryCodes({ codes, fresh, onDone }: { codes: string[]; fresh: boolean; onDone: () => void }) {
  const text = codes.join('\n');
  function download() {
    const blob = new Blob([`doof.lol recovery codes\nEach code works once.\n\n${text}\n`], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'doof-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  }
  return (
    <div className="space-y-3">
      <div className="text-xs text-text">
        {fresh ? 'Two-factor login is on. ' : ''}
        <strong>Save these recovery codes now.</strong> If you lose your phone, each one gets you in once. They won't be
        shown again.
      </div>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1 bevel-down p-3 font-mono text-sm text-text" aria-label="Recovery codes">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={() => {
            void navigator.clipboard
              .writeText(text)
              .then(() => toast.success('Recovery codes copied'))
              .catch((err) => toast.error(err, 'Could not copy'));
          }}
        >
          Copy
        </Button>
        <Button type="button" onClick={download}>
          Download .txt
        </Button>
        <Button type="button" variant="primary" onClick={onDone}>
          I've saved them
        </Button>
      </div>
    </div>
  );
}

export function TwoFactorSettings() {
  const [status, setStatus] = useState<Status | null>(null);
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      setStatus(await api.twoFactorStatus());
    } catch (err) {
      toast.error(err, 'Could not load two-factor status');
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  function go(next: Step) {
    setStep(next);
    setPassword('');
    setCode('');
    setError(null);
  }

  async function run(fn: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errText(err, fallback));
    } finally {
      setBusy(false);
    }
  }

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.twoFactorSetup(password);
      go({ kind: 'scan', ...res });
    }, 'Could not start setup');
  };

  const submitEnable = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.twoFactorEnable(code);
      go({ kind: 'codes', codes: res.recoveryCodes, fresh: true });
      void refresh();
    }, 'Could not turn on two-factor login');
  };

  const submitRegenerate = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.twoFactorRegenerateCodes(code);
      go({ kind: 'codes', codes: res.recoveryCodes, fresh: false });
      void refresh();
    }, 'Could not make new codes');
  };

  const submitDisable = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.twoFactorDisable({ password, code });
      toast.success('Two-factor login is off');
      go({ kind: 'idle' });
      void refresh();
    }, 'Could not turn off two-factor login');
  };

  /** `allowRecovery`: also accept an 8-char recovery code (turning 2FA off
   *  is exactly when someone may have lost their phone). */
  const codeInput = (label = 'Code from your authenticator app', allowRecovery = false) => (
    <Input
      label={label}
      name="totp"
      value={code}
      onChange={(e) => setCode(e.target.value)}
      {...(allowRecovery
        ? { autoComplete: 'one-time-code', maxLength: 16, placeholder: '123456 or xxxx-xxxx' }
        : { inputMode: 'numeric' as const, autoComplete: 'one-time-code', pattern: '[0-9 ]{6,7}', maxLength: 7, placeholder: '123456' })}
      required
      autoFocus={!allowRecovery}
    />
  );
  const actions = (submitLabel: string, variant: 'primary' | 'danger' = 'primary') => (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <Button type="submit" variant={variant} disabled={busy}>
        {busy ? <Spinner size={14} /> : submitLabel}
      </Button>
      <Button type="button" onClick={() => go({ kind: 'idle' })} disabled={busy}>
        Cancel
      </Button>
      {error && <span className="text-xs text-danger" role="alert">{error}</span>}
    </div>
  );

  return (
    <div className="border-t border-border-soft pt-3 mt-4">
      <div className="text-sm text-text font-medium mb-1">Two-factor login</div>
      <div className="text-xs text-text-muted mb-3">
        Ask for a code from an authenticator app (Google Authenticator, 1Password, Authy…) when you sign in, so a
        stolen password isn't enough.
      </div>

      {status === null ? (
        <Spinner size={16} />
      ) : step.kind === 'codes' ? (
        <RecoveryCodes codes={step.codes} fresh={step.fresh} onDone={() => go({ kind: 'idle' })} />
      ) : step.kind === 'password' ? (
        <form onSubmit={submitPassword} className="space-y-3">
          <Input
            label="Confirm your password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
          />
          {actions('Continue')}
        </form>
      ) : step.kind === 'scan' ? (
        <form onSubmit={submitEnable} className="space-y-3">
          <div className="text-xs text-text">1. Scan this with your authenticator app:</div>
          <div className="flex flex-wrap items-start gap-4">
            <QrCode text={step.otpauthUrl} />
            <div className="flex-1 min-w-[10rem] space-y-2 text-xs text-text-muted">
              <a className="aim-link block" href={step.otpauthUrl}>
                On this phone? Open in your authenticator app
              </a>
              <div>Or enter this key by hand:</div>
              <code className="block font-mono text-text wrap-anywhere select-all">
                {step.secret.match(/.{1,4}/g)?.join(' ')}
              </code>
            </div>
          </div>
          <div className="text-xs text-text">2. Enter the 6-digit code it shows:</div>
          {codeInput('Code')}
          {actions('Turn on')}
        </form>
      ) : step.kind === 'regenerate' ? (
        <form onSubmit={submitRegenerate} className="space-y-3">
          <div className="text-xs text-text-muted">Your old recovery codes stop working as soon as new ones are made.</div>
          {codeInput()}
          {actions('Make new codes')}
        </form>
      ) : step.kind === 'disable' ? (
        <form onSubmit={submitDisable} className="space-y-3">
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {codeInput('Code from your app, or a recovery code', true)}
          {actions('Turn off', 'danger')}
        </form>
      ) : status.enabled ? (
        <div className="space-y-3">
          <div className="text-xs text-text">
            <span className="text-online font-semibold">On</span>
            {status.enabledAt && <> since {new Date(status.enabledAt).toLocaleDateString()}</>} ·{' '}
            <span className={status.recoveryCodesLeft <= 3 ? 'text-danger font-medium' : ''}>
              {status.recoveryCodesLeft} recovery code{status.recoveryCodesLeft === 1 ? '' : 's'} left
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => go({ kind: 'regenerate' })}>
              New recovery codes
            </Button>
            <Button type="button" variant="danger" onClick={() => go({ kind: 'disable' })}>
              Turn off
            </Button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="primary" onClick={() => go({ kind: 'password' })}>
          Set up two-factor login
        </Button>
      )}
    </div>
  );
}
